import moment from 'moment'

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  SchoolYearsFilter,
  classesListFilterSelection,
  customerTypeFilterSelection,
  orgsFilterSelection,
  periodFilter,
  schoolYearFilter,
  schoolYearFilterSelection,
} from 'app/dataProviders/filter-items'

import { PlainDatePeriodBox } from 'app/common/ui-next/_filter-compilations/plain-date-period'
import { subscibeTo, useDataBoxSelector } from 'app/common/data-box-store'
import { getDisabledDaysFunc } from 'app/common/ui-next/plain-date-period'
import SimpleDate from 'app/common/classes/simple-date'
import { getID } from 'app/common/ui-next/plain-selector'

const dateFormat = 'DD-MM-YYYY' as const

const defaultPeriod = ([from, to]: [string, string]): [string, string] => {
  const currentDate = moment()
  const fromDate = new SimpleDate(from, dateFormat)
  const toDate = new SimpleDate(to, dateFormat)
  const curYear = currentDate.isBetween(fromDate.createMoment(), toDate.createMoment(), 'day')
  if (fromDate && toDate && !curYear) {
    const firstDateMonth = toDate.createMoment().startOf(SimpleDate.units.month).format(dateFormat)
    return [firstDateMonth, to]
  }
  const firstDayOfMonth = moment().startOf('month')

  const defaultDateFrom = firstDayOfMonth.format(dateFormat)
  const currentDateTo = currentDate.format(dateFormat)
  return [defaultDateFrom, currentDateTo]
}

const getEnabledDates = (
  schooltyearList: SchoolYearsFilter,
  curIndex: number,
): [string, string] => {
  const currentDate = moment()
  const currentDateTo = currentDate.format(dateFormat)
  if (curIndex < 0) {
    return defaultPeriod(['', ''])
  }
  const selectedYear = schooltyearList[curIndex]
  const prevYear = schooltyearList[curIndex - 1]
  const curYear = currentDate.isBetween(
    selectedYear.dateStart.createMoment(),
    selectedYear.dateFinish.createMoment(),
    'day',
  )
  const maxDate = curYear ? currentDateTo : selectedYear.dateFinish.getString(dateFormat)

  if (prevYear) {
    const firstNextDay = prevYear.dateFinish.createMoment().add(1, 'day').format(dateFormat)
    return [firstNextDay, maxDate]
  } else {
    return [selectedYear.dateStart.getString(dateFormat), maxDate]
  }
}

type Props = {
  id: string
  pending: boolean
}

export const PeriodFilterUnlimited = ({ id, pending }: Props) => {
  const [isSchoolYearReady, setSchoolYearReady] = useState<boolean>(false)
  const [isOrgReady, setOrgReady] = useState<boolean>(false)

  const [enabledDates, setEnabledDates] = useState<[string, string]>(['', ''])

  const setDefaultPeriod = useCallback((curPeriod: [string, string]) => {
    periodFilter.setDefault(curPeriod)
    periodFilter.updateData(() => curPeriod)
  }, [])

  const handleSchoolYearFilter = useCallback(
    ([schoolYearID]: Array<string>) => {
      if (schoolYearID) {
        const schoolyearList = schoolYearFilter.getData()
        const schoolYearSelectedIndex = schoolyearList.findIndex(
          (item) => getID(item) === schoolYearID,
        )
        const currentEnabled = getEnabledDates(schoolyearList, schoolYearSelectedIndex)
        setEnabledDates(currentEnabled)
        const currentPeriod = defaultPeriod(currentEnabled)
        setDefaultPeriod(currentPeriod)
        setSchoolYearReady(true)
        return
      }
      periodFilter.resetToDefault()
      setSchoolYearReady(false)
    },
    [setDefaultPeriod],
  )

  const handleOrgsFilter = useCallback((data: string[]) => {
    if (data.length === 1 && data[0] !== 'empty') {
      setOrgReady(true)
      return
    }
    periodFilter.resetToDefault()
    setOrgReady(false)
  }, [])

  const dateDisabledStatus = useMemo(() => {
    const [dateStart, dateFinish] = enabledDates
    if (dateStart && dateFinish) {
      const start = new SimpleDate(dateStart, dateFormat)
      const finish = new SimpleDate(dateFinish, dateFormat)
      const res = getDisabledDaysFunc(start.getAsISO8601(), finish.getAsISO8601())
      return res
    }
    return () => false
  }, [enabledDates])

  useEffect(() => {
    const currentPeriod = defaultPeriod(enabledDates)
    setDefaultPeriod(currentPeriod)
  }, [enabledDates, setDefaultPeriod])

  useEffect(() => {
    const schoolYearUnsubsribe = subscibeTo<string[]>(
      schoolYearFilterSelection.id,
      handleSchoolYearFilter,
    )
    const orgsUnsubsribe = subscibeTo<string[]>(orgsFilterSelection.id, handleOrgsFilter)

    return () => {
      schoolYearUnsubsribe()
      orgsUnsubsribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    isOrgReady &&
    isSchoolYearReady && (
      <PlainDatePeriodBox
        id={id}
        label="Период времени"
        loading={pending}
        dateFormat={dateFormat}
        dataBoxData={periodFilter}
        periodLimit={365}
        dateDisabledStatus={dateDisabledStatus}
      />
    )
  )
}
